import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const PUBLIC = process.env.PUBLIC_URL || '';
const INTRO_MS = 3600;
const SESSION_KEY = 'acctoo_splash_played_v1';

const TAGLINE = 'Simplifying Accounting, Together';
const LETTERS = ['A', 'c', 'c', 'T', 'o', 'o'];
const FEATURES = ['Sales', 'GST', 'Reports', 'Cash Flow'];

/**
 * Cinematic app opening built entirely with CSS motion.
 */
const AcctooSplashIntro = ({ onComplete, forcePlay = false }) => {
  const completedRef = useRef(false);
  const [phase, setPhase] = useState('boot');
  const [logoFailed, setLogoFailed] = useState(false);
  const [skipping, setSkipping] = useState(false);

  const reducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  const particles = useMemo(
    () =>
      Array.from({ length: 28 }, (_, i) => ({
        id: i,
        left: `${8 + ((i * 37) % 84)}%`,
        top: `${6 + ((i * 53) % 88)}%`,
        size: 4 + (i % 5) * 2,
        delay: `${(i % 7) * 0.12}s`,
        duration: `${2.4 + (i % 4) * 0.35}s`,
      })),
    []
  );

  const finish = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      /* ignore */
    }
    onComplete?.();
  }, [onComplete]);

  const handleSkip = useCallback(() => {
    setSkipping(true);
    window.setTimeout(finish, 280);
  }, [finish]);

  useEffect(() => {
    if (forcePlay) return;
    try {
      if (sessionStorage.getItem(SESSION_KEY) === '1') {
        finish();
      }
    } catch {
      /* ignore */
    }
  }, [forcePlay, finish]);

  useEffect(() => {
    if (reducedMotion) {
      finish();
      return undefined;
    }
    setPhase('animated');
    return undefined;
  }, [reducedMotion, finish]);

  useEffect(() => {
    if (phase !== 'animated') return undefined;

    const t1 = window.setTimeout(() => setPhase('animated-reveal'), 120);
    const t2 = window.setTimeout(() => setPhase('animated-tagline'), 950);
    const t3 = window.setTimeout(() => setPhase('animated-out'), INTRO_MS - 420);
    const t4 = window.setTimeout(finish, INTRO_MS);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      window.clearTimeout(t4);
    };
  }, [phase, finish]);

  if (phase === 'boot') {
    return (
      <div className="acctoo-splash fixed inset-0 z-[10001] bg-slate-950" aria-hidden />
    );
  }

  const showLetters = phase === 'animated-reveal' || phase === 'animated-tagline' || phase === 'animated-out';
  const showTagline = phase === 'animated-tagline' || phase === 'animated-out';
  const isOut = phase === 'animated-out' || skipping;

  return (
    <div
      className={`acctoo-splash fixed inset-0 z-[10001] overflow-hidden transition-opacity duration-500 ${isOut ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
      role="dialog"
      aria-label="AccToo opening"
      onClick={handleSkip}
    >
      <div className="acctoo-splash-cinema" aria-hidden />
      <div className="acctoo-splash-rays" aria-hidden />
      <div className="acctoo-splash-spotlight" aria-hidden />
      <div className="acctoo-splash-horizon" aria-hidden />
      <div className="acctoo-splash-scanline" aria-hidden />
      <div className="acctoo-splash-vignette" aria-hidden />

      {particles.map((p) => (
        <span
          key={p.id}
          className="acctoo-splash-particle"
          style={{
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            animationDelay: p.delay,
            animationDuration: p.duration,
          }}
          aria-hidden
        />
      ))}

      <div className="relative z-10 flex h-full w-full flex-col items-center justify-center px-6 text-center">
        <div className={`acctoo-splash-feature-strip ${showTagline ? 'acctoo-splash-feature-strip--in' : ''}`}>
          {FEATURES.map((feature, index) => (
            <span
              key={feature}
              className="acctoo-splash-feature-pill"
              style={{ animationDelay: `${0.65 + index * 0.12}s` }}
            >
              {feature}
            </span>
          ))}
        </div>

        <div className={`acctoo-splash-logo-wrap ${showLetters ? 'acctoo-splash-logo-wrap--in' : ''}`}>
          <div className="acctoo-splash-ring acctoo-splash-ring--a" />
          <div className="acctoo-splash-ring acctoo-splash-ring--b" />
          <div className="acctoo-splash-ring acctoo-splash-ring--c" />
          <div className="acctoo-splash-logo-core">
            {!logoFailed ? (
              <img
                src={`${PUBLIC}/acctoo-logo.png`}
                alt=""
                className="h-16 w-16 object-contain drop-shadow-[0_8px_24px_rgba(56,189,248,0.55)]"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <span className="font-acctoo-display text-4xl font-black text-white">A</span>
            )}
          </div>
        </div>

        <h1
          className={`acctoo-splash-title mt-8 flex justify-center gap-0.5 sm:gap-1 ${showLetters ? 'acctoo-splash-title--in' : ''}`}
          aria-label="AccToo"
        >
          {LETTERS.map((ch, i) => (
            <span
              key={`${ch}-${i}`}
              className="acctoo-splash-letter"
              style={{ animationDelay: `${i * 0.07}s` }}
            >
              {ch}
            </span>
          ))}
        </h1>

        <p className={`acctoo-splash-tagline mt-4 max-w-xs text-sm sm:text-base ${showTagline ? 'acctoo-splash-tagline--in' : ''}`}>
          {TAGLINE}
        </p>

        <div className={`acctoo-splash-loader mt-8 w-56 max-w-[72vw] ${showLetters ? 'acctoo-splash-loader--in' : ''}`}>
          <div className="acctoo-splash-loader-track">
            <div className="acctoo-splash-loader-fill" />
          </div>
        </div>

        <p className="acctoo-splash-tap mt-5 text-xs text-white/50">Tap to skip</p>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            handleSkip();
          }}
          className="absolute top-[max(1rem,env(safe-area-inset-top))] right-4 z-10 min-h-[44px] px-4 rounded-full bg-white/10 text-white text-sm font-medium backdrop-blur-md border border-white/15 touch-manipulation"
        >
          Skip
        </button>
      </div>

      <style>{`
        .acctoo-splash {
          --sky: #38bdf8;
          --cyan: #22d3ee;
          --indigo: #818cf8;
          --violet: #a78bfa;
        }

        .acctoo-splash-cinema {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 90% 70% at 50% 35%, rgba(56, 189, 248, 0.28) 0%, transparent 55%),
            radial-gradient(ellipse 60% 50% at 80% 80%, rgba(129, 140, 248, 0.22) 0%, transparent 50%),
            linear-gradient(165deg, #020617 0%, #0c4a6e 38%, #1e1b4b 72%, #020617 100%);
          animation: acctoo-splash-bg-pulse 3.2s ease-in-out infinite;
        }

        .acctoo-splash-rays {
          position: absolute;
          inset: -50%;
          background: conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(56,189,248,0.08) 30deg, transparent 60deg, rgba(129,140,248,0.06) 120deg, transparent 150deg);
          animation: acctoo-splash-spin 18s linear infinite;
        }

        .acctoo-splash-spotlight {
          position: absolute;
          left: 50%;
          top: -18%;
          width: min(140vw, 54rem);
          height: 78vh;
          transform: translateX(-50%);
          background: radial-gradient(ellipse at top, rgba(255,255,255,0.14) 0%, rgba(56,189,248,0.12) 18%, transparent 62%);
          filter: blur(8px);
          animation: acctoo-splash-spotlight 3.6s ease-in-out infinite;
        }

        .acctoo-splash-horizon {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 16%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(34,211,238,0.8), rgba(129,140,248,0.85), transparent);
          box-shadow: 0 0 24px rgba(34,211,238,0.5);
        }

        .acctoo-splash-scanline {
          position: absolute;
          inset: 0;
          background: repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(255,255,255,0.015) 2px,
            rgba(255,255,255,0.015) 4px
          );
          pointer-events: none;
          opacity: 0.35;
        }

        .acctoo-splash-vignette {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at center, transparent 38%, rgba(2,6,23,0.1) 55%, rgba(2,6,23,0.72) 100%);
          pointer-events: none;
        }

        .acctoo-splash-particle {
          position: absolute;
          border-radius: 9999px;
          background: linear-gradient(135deg, var(--sky), var(--cyan));
          box-shadow: 0 0 12px rgba(56, 189, 248, 0.65);
          animation-name: acctoo-splash-float;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
          opacity: 0;
        }

        .acctoo-splash-logo-wrap {
          position: relative;
          width: 7.5rem;
          height: 7.5rem;
          transform: scale(0.2);
          opacity: 0;
          margin-top: 0.5rem;
        }

        .acctoo-splash-logo-wrap--in {
          animation: acctoo-splash-logo-pop 0.85s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        .acctoo-splash-ring {
          position: absolute;
          inset: 0;
          border-radius: 9999px;
          border: 2px solid transparent;
        }

        .acctoo-splash-ring--a {
          border-top-color: var(--sky);
          border-right-color: var(--cyan);
          animation: acctoo-splash-spin 2.2s linear infinite;
        }

        .acctoo-splash-ring--b {
          inset: 0.5rem;
          border-bottom-color: var(--indigo);
          border-left-color: var(--violet);
          animation: acctoo-splash-spin 1.6s linear infinite reverse;
        }

        .acctoo-splash-ring--c {
          inset: 1rem;
          border: 1px dashed rgba(255,255,255,0.2);
          animation: acctoo-splash-spin 8s linear infinite;
        }

        .acctoo-splash-logo-core {
          position: absolute;
          inset: 1.35rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 1.25rem;
          background: rgba(255,255,255,0.12);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.25);
          box-shadow: 0 0 40px rgba(56,189,248,0.35);
        }

        .acctoo-splash-title {
          opacity: 0;
          transform: translateY(12px);
        }

        .acctoo-splash-title--in {
          animation: acctoo-splash-rise 0.6s ease forwards 0.15s;
        }

        .acctoo-splash-letter {
          display: inline-block;
          font-family: Poppins, system-ui, sans-serif;
          font-size: clamp(2.25rem, 9vw, 3.25rem);
          font-weight: 800;
          background: linear-gradient(135deg, #fff 0%, var(--cyan) 45%, var(--indigo) 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          opacity: 0;
          transform: translateY(24px) scale(0.6);
          animation: acctoo-splash-letter-in 0.55s cubic-bezier(0.34, 1.4, 0.64, 1) forwards;
          text-shadow: 0 0 30px rgba(56,189,248,0.3);
        }

        .acctoo-splash-tagline {
          color: rgba(255,255,255,0.72);
          letter-spacing: 0.04em;
          opacity: 0;
          transform: translateY(10px);
        }

        .acctoo-splash-tagline--in {
          animation: acctoo-splash-rise 0.55s ease forwards;
        }

        .acctoo-splash-feature-strip {
          min-height: 2rem;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem;
          opacity: 0;
          transform: translateY(-8px);
          margin-bottom: 1.25rem;
        }

        .acctoo-splash-feature-strip--in {
          animation: acctoo-splash-rise 0.45s ease forwards;
        }

        .acctoo-splash-feature-pill {
          display: inline-flex;
          align-items: center;
          min-height: 1.75rem;
          padding: 0.2rem 0.7rem;
          border-radius: 9999px;
          border: 1px solid rgba(255,255,255,0.16);
          background: rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.75);
          font-size: 0.7rem;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          opacity: 0;
          transform: scale(0.9) translateY(8px);
          animation: acctoo-splash-pill-in 0.45s ease forwards;
          backdrop-filter: blur(6px);
        }

        .acctoo-splash-loader {
          opacity: 0;
        }

        .acctoo-splash-loader--in {
          animation: acctoo-splash-rise 0.4s ease forwards 0.2s;
        }

        .acctoo-splash-loader-track {
          height: 4px;
          border-radius: 9999px;
          background: rgba(255,255,255,0.12);
          overflow: hidden;
        }

        .acctoo-splash-loader-fill {
          height: 100%;
          width: 0;
          border-radius: inherit;
          background: linear-gradient(90deg, var(--sky), var(--cyan), var(--indigo), var(--violet));
          animation: acctoo-splash-progress ${INTRO_MS - 600}ms ease-out forwards 0.4s;
        }

        @keyframes acctoo-splash-bg-pulse {
          0%, 100% { filter: brightness(1); }
          50% { filter: brightness(1.08); }
        }

        @keyframes acctoo-splash-spin {
          to { transform: rotate(360deg); }
        }

        @keyframes acctoo-splash-spotlight {
          0%, 100% { transform: translateX(-50%) scaleX(0.95); opacity: 0.8; }
          50% { transform: translateX(-50%) scaleX(1.08); opacity: 1; }
        }

        @keyframes acctoo-splash-float {
          0% { opacity: 0; transform: translateY(8px) scale(0.5); }
          20% { opacity: 0.9; }
          80% { opacity: 0.5; }
          100% { opacity: 0; transform: translateY(-40px) scale(1.1); }
        }

        @keyframes acctoo-splash-logo-pop {
          0% { opacity: 0; transform: scale(0.2); }
          60% { opacity: 1; transform: scale(1.08); }
          100% { opacity: 1; transform: scale(1); }
        }

        @keyframes acctoo-splash-rise {
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes acctoo-splash-letter-in {
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes acctoo-splash-pill-in {
          to { opacity: 1; transform: scale(1) translateY(0); }
        }

        @keyframes acctoo-splash-progress {
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default AcctooSplashIntro;
